import { ClockIcon } from "lucide-react";
import React, { forwardRef, useState } from "react";

import { cn } from "@/lib/utils";

export type TimePickerInputProps = {
  readonly id?: string;
  readonly name: string;
  readonly value: string;
  readonly placeholder?: string;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  readonly onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby": string;
  readonly "data-testid"?: string;
  readonly minTime?: string;
  readonly maxTime?: string;
};

const TimePickerInput = forwardRef<HTMLInputElement, TimePickerInputProps>(
  (props, ref): React.ReactElement => {
    const {
      id,
      name,
      placeholder = "HH:MM",
      className = "",
      disabled,
      onBlur,
      onFocus,
      onChange,
      value,
      minTime,
      maxTime,
      "data-testid": dataTestId,
    } = props;

    return (
      <div className="TimePickerInput relative" data-testid={dataTestId}>
        <div className="absolute left-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center">
          <ClockIcon className="h-4 w-4 text-secondary" />
        </div>
        <input
          aria-describedby={props["aria-describedby"]}
          aria-invalid={props["aria-invalid"]}
          className={cn(
            "h-10",
            "-ring-offset-1 peer block w-full appearance-none rounded border-none px-3 ring-1 ring-light-grey focus:outline-none focus:ring-1 focus:!ring-blue-400 aria-invalid:ring-1 aria-invalid:ring-red aria-invalid:placeholder:text-red",
            "placeholder:!text-secondary",
            "pl-9",
            className,
            disabled && "pointer-events-none opacity-50"
          )}
          disabled={disabled}
          id={id}
          inputMode="numeric"
          name={name}
          placeholder={placeholder}
          ref={ref}
          type="text"
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          maxLength={5}
        />
      </div>
    );
  }
);

TimePickerInput.displayName = "TimePickerInput";

export default TimePickerInput;
